import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Clock, Users, Home, Stethoscope, Calendar, 
  Heart, Shield, CheckCircle, Phone, ArrowRight 
} from 'lucide-react';

const TreatmentTypesPage = () => {
  const treatmentTypes = [
    {
      id: 'inpatient',
      title: 'Inpatient Treatment',
      icon: <Home className="h-8 w-8" />,
      duration: '30-90 days',
      level: 'Highest intensity',
      description: 'Residential treatment with 24/7 medical supervision and comprehensive care in a controlled environment.',
      features: [
        '24/7 medical supervision',
        'Structured daily schedule',
        'Individual and group therapy',
        'Medical detox available',
        'Peer support programs',
        'Family therapy sessions'
      ],
      bestFor: [
        'Severe addiction',
        'Multiple failed attempts',
        'Co-occurring disorders',
        'Need for detox',
        'Unstable living environment'
      ],
      color: 'blue'
    },
    {
      id: 'outpatient',
      title: 'Outpatient Treatment',
      icon: <Users className="h-8 w-8" />,
      duration: '3-12 months',
      level: 'Flexible intensity',
      description: 'Treatment while living at home, allowing you to maintain work, school, and family responsibilities.',
      features: [
        'Flexible scheduling',
        'Individual counseling',
        'Group therapy sessions',
        'Family involvement',
        'Medication management',
        'Relapse prevention'
      ],
      bestFor: [
        'Stable living environment',
        'Strong support system',
        'Work/school commitments',
        'Mild to moderate addiction',
        'After inpatient treatment'
      ],
      color: 'green'
    },
    {
      id: 'iop',
      title: 'Intensive Outpatient (IOP)',
      icon: <Calendar className="h-8 w-8" />,
      duration: '3-6 months',
      level: 'Moderate intensity',
      description: 'More intensive than regular outpatient but less than residential, typically 9-20 hours per week.',
      features: [
        '9-20 hours per week',
        'Evening/weekend options',
        'Group and individual therapy',
        'Educational workshops',
        'Medication monitoring',
        'Crisis intervention'
      ],
      bestFor: [
        'Step-down from inpatient',
        'Need structure but flexibility',
        'Moderate addiction severity',
        'Strong motivation',
        'Supportive environment'
      ],
      color: 'purple'
    },
    {
      id: 'php',
      title: 'Partial Hospitalization (PHP)',
      icon: <Stethoscope className="h-8 w-8" />,
      duration: '2-8 weeks',
      level: 'High intensity',
      description: 'Day treatment program providing intensive care without overnight stays, typically 20-30 hours per week.',
      features: [
        '20-30 hours per week',
        'Medical monitoring',
        'Psychiatric services',
        'Individual therapy',
        'Group counseling',
        'Medication management'
      ],
      bestFor: [
        'Need intensive support',
        'Medical complications',
        'Psychiatric conditions',
        'After detox',
        'Before or after inpatient'
      ],
      color: 'orange'
    },
    {
      id: 'detox',
      title: 'Medical Detoxification',
      icon: <Heart className="h-8 w-8" />,
      duration: '3-10 days',
      level: 'Medical supervision',
      description: 'Medically supervised withdrawal management to safely remove substances from your body.',
      features: [
        '24/7 medical monitoring',
        'Medication-assisted treatment',
        'Withdrawal symptom management',
        'Nutritional support',
        'Emotional support',
        'Treatment planning'
      ],
      bestFor: [
        'Physical dependence',
        'Severe withdrawal symptoms',
        'Medical complications',
        'First step in treatment',
        'Safety concerns'
      ],
      color: 'red'
    },
    {
      id: 'sober-living',
      title: 'Sober Living',
      icon: <Shield className="h-8 w-8" />,
      duration: '3-12+ months',
      level: 'Supportive environment',
      description: 'Transitional housing with structure and support while you rebuild your life in recovery.',
      features: [
        'Drug and alcohol-free environment',
        'House rules and structure',
        'Peer support community',
        'Life skills development',
        'Employment assistance',
        'Ongoing recovery support'
      ],
      bestFor: [
        'After treatment completion',
        'Need structured environment',
        'Building independent skills',
        'Peer support benefits',
        'Transitional phase'
      ],
      color: 'teal'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'text-blue-600 bg-blue-100',
      green: 'text-green-600 bg-green-100',
      purple: 'text-purple-600 bg-purple-100',
      orange: 'text-orange-600 bg-orange-100',
      red: 'text-red-600 bg-red-100',
      teal: 'text-teal-600 bg-teal-100'
    };
    return colors[color] || 'text-gray-600 bg-gray-100';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-purple-600 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Types of Addiction Treatment
            </h1>
            <p className="text-xl text-blue-100 mb-8">
              Understanding different levels of care to find the right treatment approach for your unique situation and recovery goals.
            </p>
            
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-4 mb-8">
              <Phone className="h-5 w-5 mr-3" />
              <span className="mr-2">Get help choosing the right treatment:</span>
              <a 
                href="tel:1-800-662-4357"
                className="text-xl font-bold hover:underline"
              >
                1-800-662-HELP
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Types Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Choose the Right Level of Care
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Each treatment type offers different levels of support and structure. 
                The right choice depends on your addiction severity, life circumstances, and recovery goals.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {treatmentTypes.map((type) => (
                <Card key={type.id} className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className={`p-3 rounded-full ${getColorClasses(type.color)}`}>
                        {type.icon}
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="mb-1">
                          {type.duration}
                        </Badge>
                        <div className="text-sm text-gray-600">{type.level}</div>
                      </div>
                    </div>
                    
                    <CardTitle className="text-xl mb-2">{type.title}</CardTitle>
                    <p className="text-gray-600 text-sm">{type.description}</p>
                  </CardHeader>

                  <CardContent className="space-y-6">
                    {/* Features */}
                    <div>
                      <h4 className="font-semibold mb-3 text-gray-900">What's Included:</h4>
                      <div className="grid grid-cols-1 gap-2">
                        {type.features.map((feature) => (
                          <div key={feature} className="flex items-center text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Best For */}
                    <div>
                      <h4 className="font-semibold mb-3 text-gray-900">Best For:</h4>
                      <div className="flex flex-wrap gap-2">
                        {type.bestFor.map((item) => (
                          <Badge key={item} variant="secondary" className="text-xs">
                            {item}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="pt-4 border-t">
                      <Button asChild className="w-full">
                        <Link to={`/search?treatment=${type.id}`}>
                          Find {type.title} Programs
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Selection Guide */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-center mb-8">
              How to Choose the Right Treatment Level
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Stethoscope className="h-5 w-5 mr-2 text-blue-600" />
                    Assessment Factors
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <div>
                      <div className="font-medium text-sm">Addiction Severity</div>
                      <div className="text-xs text-gray-600">How long and severe is the substance use?</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <div>
                      <div className="font-medium text-sm">Medical Needs</div>
                      <div className="text-xs text-gray-600">Do you need detox or medical monitoring?</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <div>
                      <div className="font-medium text-sm">Support System</div>
                      <div className="text-xs text-gray-600">What support do you have at home?</div>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                    <div>
                      <div className="font-medium text-sm">Life Responsibilities</div>
                      <div className="text-xs text-gray-600">Work, family, or school obligations?</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Users className="h-5 w-5 mr-2 text-purple-600" />
                    Professional Guidance
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Choosing the right treatment level is crucial for your success. 
                    Our treatment coordinators can help you assess your needs and 
                    find the most appropriate program.
                  </p>
                  
                  <div className="space-y-2">
                    <Button asChild className="w-full">
                      <a href="tel:1-800-662-4357">
                        <Phone className="h-4 w-4 mr-2" />
                        Call for Assessment: 1-800-662-HELP
                      </a>
                    </Button>
                    
                    <Button asChild variant="outline" className="w-full">
                      <Link to="/get-help">
                        Get Help Online
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Start Your Recovery Journey?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Don't wait to get the help you need. Contact us today to discuss 
            your treatment options and find the right program for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              <a href="tel:1-800-662-4357">
                <Phone className="h-5 w-5 mr-2" />
                Call Now: 1-800-662-HELP
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-blue-600">
              <Link to="/search">
                Find Treatment Centers
                <ArrowRight className="h-4 w-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TreatmentTypesPage;